/**
 * Quick Start Templates - GarageBand Inspired
 * Pre-configured project templates for different genres and use cases
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Music,
  Mic2,
  Guitar,
  Drum,
  Radio,
  Sparkles,
  X,
  Play,
  ChevronRight,
  Headphones,
  Podcast,
  Music2,
  Waves,
} from 'lucide-react';

// Template definitions
export const TEMPLATES = {
  blank: {
    id: 'blank',
    name: 'Blank Project',
    description: 'Start from scratch with empty tracks',
    icon: Music,
    color: 'from-zinc-500 to-zinc-600',
    bpm: 120,
    tracks: [
      { name: 'Track 1', type: 'audio', color: 'from-cyan-500 to-cyan-600' },
      { name: 'Track 2', type: 'audio', color: 'from-purple-500 to-purple-600' },
      { name: 'Track 3', type: 'audio', color: 'from-amber-500 to-amber-600' },
      { name: 'Track 4', type: 'audio', color: 'from-emerald-500 to-emerald-600' },
    ],
  },
  pop: {
    id: 'pop',
    name: 'Pop Production',
    description: 'Modern pop song setup with drums, bass, synth, and vocals',
    icon: Radio,
    color: 'from-pink-500 to-rose-500',
    bpm: 120,
    tracks: [
      { name: 'Drums', type: 'instrument', instrumentType: 'drums', color: 'from-amber-500 to-amber-600' },
      { name: 'Bass', type: 'instrument', instrumentType: 'synth', color: 'from-indigo-500 to-indigo-600' },
      { name: 'Synth Lead', type: 'instrument', instrumentType: 'synth', color: 'from-purple-500 to-purple-600' },
      { name: 'Synth Pad', type: 'instrument', instrumentType: 'synth', color: 'from-cyan-500 to-cyan-600' },
      { name: 'Vocals', type: 'audio', color: 'from-rose-500 to-rose-600' },
      { name: 'Backing Vocals', type: 'audio', color: 'from-pink-500 to-pink-600' },
    ],
  },
  hiphop: {
    id: 'hiphop',
    name: 'Hip-Hop Beat',
    description: 'Classic hip-hop setup with 808s, samples, and vocal tracks',
    icon: Headphones,
    color: 'from-orange-500 to-red-500',
    bpm: 90,
    tracks: [
      { name: '808 Drums', type: 'instrument', instrumentType: 'drums', color: 'from-red-500 to-red-600' },
      { name: '808 Bass', type: 'instrument', instrumentType: 'synth', color: 'from-orange-500 to-orange-600' },
      { name: 'Hi-Hats', type: 'instrument', instrumentType: 'drums', color: 'from-amber-500 to-amber-600' },
      { name: 'Sample 1', type: 'audio', color: 'from-purple-500 to-purple-600' },
      { name: 'Vocals', type: 'audio', color: 'from-cyan-500 to-cyan-600' },
      { name: 'Ad-libs', type: 'audio', color: 'from-emerald-500 to-emerald-600' },
    ],
  },
  edm: {
    id: 'edm',
    name: 'Electronic/EDM',
    description: 'High-energy EDM template with synths and build-up tracks',
    icon: Waves,
    color: 'from-cyan-500 to-blue-500',
    bpm: 128,
    tracks: [
      { name: 'Kick', type: 'instrument', instrumentType: 'drums', color: 'from-red-500 to-red-600' },
      { name: 'Clap/Snare', type: 'instrument', instrumentType: 'drums', color: 'from-amber-500 to-amber-600' },
      { name: 'Hi-Hats', type: 'instrument', instrumentType: 'drums', color: 'from-yellow-500 to-yellow-600' },
      { name: 'Bass Drop', type: 'instrument', instrumentType: 'synth', color: 'from-indigo-500 to-indigo-600' },
      { name: 'Lead Synth', type: 'instrument', instrumentType: 'synth', color: 'from-cyan-500 to-cyan-600' },
      { name: 'Pad/Atmosphere', type: 'instrument', instrumentType: 'synth', color: 'from-purple-500 to-purple-600' },
      { name: 'FX/Risers', type: 'audio', color: 'from-emerald-500 to-emerald-600' },
      { name: 'Vocals', type: 'audio', color: 'from-pink-500 to-pink-600' },
    ],
  },
  lofi: {
    id: 'lofi',
    name: 'Lo-Fi Chill',
    description: 'Relaxed lo-fi beats with jazzy chords and vinyl texture',
    icon: Music2,
    color: 'from-violet-500 to-purple-500',
    bpm: 75,
    tracks: [
      { name: 'Drums (Lo-Fi)', type: 'instrument', instrumentType: 'drums', color: 'from-amber-500 to-amber-600' },
      { name: 'Bass', type: 'instrument', instrumentType: 'synth', color: 'from-indigo-500 to-indigo-600' },
      { name: 'Keys/Piano', type: 'instrument', instrumentType: 'synth', color: 'from-purple-500 to-purple-600' },
      { name: 'Guitar Sample', type: 'audio', color: 'from-orange-500 to-orange-600' },
      { name: 'Vinyl Noise', type: 'audio', color: 'from-zinc-500 to-zinc-600' },
    ],
  },
  rock: {
    id: 'rock',
    name: 'Rock Band',
    description: 'Classic rock setup with guitars, bass, drums, and vocals',
    icon: Guitar,
    color: 'from-red-500 to-orange-500',
    bpm: 130,
    tracks: [
      { name: 'Drums', type: 'audio', color: 'from-amber-500 to-amber-600' },
      { name: 'Bass Guitar', type: 'audio', color: 'from-indigo-500 to-indigo-600' },
      { name: 'Rhythm Guitar', type: 'audio', color: 'from-red-500 to-red-600' },
      { name: 'Lead Guitar', type: 'audio', color: 'from-orange-500 to-orange-600' },
      { name: 'Lead Vocals', type: 'audio', color: 'from-cyan-500 to-cyan-600' },
      { name: 'Backing Vocals', type: 'audio', color: 'from-pink-500 to-pink-600' },
    ],
  },
  podcast: {
    id: 'podcast',
    name: 'Podcast/Voice',
    description: 'Multi-track podcast setup with host and guest channels',
    icon: Podcast,
    color: 'from-emerald-500 to-teal-500',
    bpm: 120,
    tracks: [
      { name: 'Host', type: 'audio', color: 'from-cyan-500 to-cyan-600' },
      { name: 'Guest 1', type: 'audio', color: 'from-purple-500 to-purple-600' },
      { name: 'Guest 2', type: 'audio', color: 'from-emerald-500 to-emerald-600' },
      { name: 'Intro/Outro Music', type: 'audio', color: 'from-amber-500 to-amber-600' },
      { name: 'Sound Effects', type: 'audio', color: 'from-rose-500 to-rose-600' },
    ],
  },
  singer: {
    id: 'singer',
    name: 'Singer-Songwriter',
    description: 'Simple setup for acoustic performances with vocals and guitar',
    icon: Mic2,
    color: 'from-amber-500 to-yellow-500',
    bpm: 100,
    tracks: [
      { name: 'Vocals', type: 'audio', color: 'from-cyan-500 to-cyan-600' },
      { name: 'Acoustic Guitar', type: 'audio', color: 'from-amber-500 to-amber-600' },
      { name: 'Piano', type: 'instrument', instrumentType: 'synth', color: 'from-purple-500 to-purple-600' },
      { name: 'Harmonies', type: 'audio', color: 'from-pink-500 to-pink-600' },
    ],
  },
};

// Template card component
const TemplateCard = ({ template, isSelected, onClick }) => {
  const IconComponent = template.icon;
  
  return (
    <motion.button
      onClick={onClick}
      className={`relative w-full p-4 rounded-xl border text-left transition-all ${
        isSelected
          ? 'bg-zinc-800 border-indigo-500 ring-2 ring-indigo-500/30'
          : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800'
      }`}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex items-start gap-3">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${template.color} flex items-center justify-center flex-shrink-0`}>
          <IconComponent className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-white text-sm">{template.name}</h4>
          <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{template.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-300">
              {template.bpm} BPM
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-300">
              {template.tracks.length} tracks
            </span>
          </div>
        </div>
        {isSelected && (
          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center">
            <Sparkles className="w-3 h-3 text-white" />
          </div>
        )}
      </div>
    </motion.button>
  );
};

// Main Quick Start Templates Modal
export const QuickStartTemplates = ({
  isOpen,
  onClose,
  onSelectTemplate,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState('blank');
  
  const handleCreate = () => {
    const template = TEMPLATES[selectedTemplate];
    if (template) {
      onSelectTemplate(template);
      onClose();
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          className="w-full max-w-3xl bg-zinc-900 rounded-2xl border border-zinc-700 shadow-2xl overflow-hidden max-h-[85vh] flex flex-col"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          data-testid="quick-start-modal"
        >
          {/* Header */}
          <div className="relative px-6 py-4 border-b border-zinc-800 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Quick Start</h2>
                <p className="text-xs text-zinc-400">Choose a template to get started quickly</p>
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors"
              data-testid="quick-start-close-btn"
            >
              <X className="w-4 h-4 text-zinc-400" />
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-2 gap-3">
              {Object.values(TEMPLATES).map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  isSelected={selectedTemplate === template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                />
              ))}
            </div>
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 border-t border-zinc-800 bg-zinc-900/80 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="text-sm text-zinc-400">
                <span className="text-white font-medium">{TEMPLATES[selectedTemplate]?.name}</span>
                {' · '}
                {TEMPLATES[selectedTemplate]?.tracks.length} tracks
                {' · '}
                {TEMPLATES[selectedTemplate]?.bpm} BPM
              </div>
              <Button
                onClick={handleCreate}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-6"
                data-testid="quick-start-create-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                Create Project
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default QuickStartTemplates;
